import type { FC } from 'react';
import { useMemo, useState } from 'react';

import { Collapsible as PrimitiveCollapsible } from '@base-ui/react/collapsible';

import { CollapsiblePanel } from './__panel/collapsible__panel.tsx';
import { CollapsibleTrigger } from './__trigger/collapsible__trigger.tsx';
import { CollapsibleContext } from './collapsible.context.ts';

export type { CollapsibleTriggerProps } from './__trigger/collapsible__trigger.tsx';
export type { CollapsiblePanelProps } from './__panel/collapsible__panel.tsx';

/**
 * Props for the Collapsible root. Controls open state and disabled behavior.
 */
export type CollapsibleProps = PrimitiveCollapsible.Root.Props;

/**
 * An interactive component that expands and collapses a section of content.
 *
 * ```tsx
 * <Collapsible defaultOpen>
 *     <Collapsible.Trigger>
 *         <Button>Toggle details</Button>
 *     </Collapsible.Trigger>
 *     <Collapsible.Panel>
 *         <Text>Additional information about the item.</Text>
 *     </Collapsible.Panel>
 * </Collapsible>
 * ```
 */
export const Collapsible: FC<CollapsibleProps> & {
    Trigger: typeof CollapsibleTrigger;
    Panel: typeof CollapsiblePanel;
} = (props) => {
    const [clipped, setClipped] = useState<boolean>();

    const contextValue = useMemo(() => ({ clipped, setClipped }), [clipped]);

    return (
        <CollapsibleContext.Provider value={contextValue}>
            <PrimitiveCollapsible.Root {...props} />
        </CollapsibleContext.Provider>
    );
};

Collapsible.Trigger = CollapsibleTrigger;
Collapsible.Panel = CollapsiblePanel;
